<template>
  <div class="skd-pagination">
    <div class="skd-pagination__summary">
      Showing {{ itemsOnPage }} of {{ totalItems }} {{ totalItems === 1 ? 'result' : 'results' }}
    </div>

    <div class="skd-pagination__controls">
      <vue-awesome-paginate
        v-if="totalItems > 0"
        :total-items="totalItems"
        :items-per-page="perPage"
        :max-pages-shown="maxPagesShown"
        :model-value="safePage"
        @update:model-value="onPageModel"
        @click="onPageClick"
      >
        <template #prev-button>
          <span class="skd-pagination__nav">
            <i class="pi pi-chevron-left"></i>
            <span>Prev</span>
          </span>
        </template>
        <template #next-button>
          <span class="skd-pagination__nav">
            <span>Next</span>
            <i class="pi pi-chevron-right"></i>
          </span>
        </template>
      </vue-awesome-paginate>

      <div class="skd-pagination__perpage">
        <div class="skd-pagination__select-wrap">
          <select
            class="skd-pagination__select"
            :value="perPage"
            aria-label="Items per page"
            @change="onPerPageChange"
          >
            <option v-for="option in perPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
          <i class="pi pi-chevron-down skd-pagination__select-icon"></i>
        </div>
        <span class="skd-pagination__perpage-label">per page</span>
      </div>
    </div>
  </div>
</template>

<script>
// Reusable, theme-aware pagination control.
//
// It is intentionally mode-agnostic: it only knows the total item count, the
// active page and the page size. The parent decides what a page/per-page change
// means — refetch from the server, or re-slice a local array — by listening to
// the `change` / `update:per-page` events. Two-way binding is supported via
// `v-model:page` and `v-model:per-page` for the simpler (client-side) callers.
//
// The control is always rendered whenever there is at least one item (the
// parent guards with v-if on the total): with a single page the page buttons
// still show, with Prev/Next disabled, so the user always sees where they are.
export default {
  name: 'Pagination',
  props: {
    totalItems: {
      type: Number,
      required: true,
    },
    page: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 20,
    },
    perPageOptions: {
      type: Array,
      default: () => [20, 50, 100, 200],
    },
    maxPagesShown: {
      type: Number,
      default: 7,
    },
  },
  emits: ['update:page', 'update:perPage', 'change', 'change-per-page'],
  computed: {
    totalPages() {
      const perPage = Math.max(Number(this.perPage) || 1, 1);
      return Math.max(Math.ceil((Number(this.totalItems) || 0) / perPage), 1);
    },
    // Keep the highlighted page inside the valid range even if the parent is
    // momentarily out of sync (e.g. right after the page size grows).
    safePage() {
      return Math.min(Math.max(Number(this.page) || 1, 1), this.totalPages);
    },
    rangeStart() {
      if (!this.totalItems) return 0;
      return Math.min(((this.safePage - 1) * this.perPage) + 1, this.totalItems);
    },
    rangeEnd() {
      return Math.min(this.safePage * this.perPage, this.totalItems);
    },
    // The count of items actually on the current page ("Showing 20 of 608").
    itemsOnPage() {
      if (!this.totalItems) return 0;
      return (this.rangeEnd - this.rangeStart) + 1;
    },
  },
  methods: {
    emitPage(value) {
      const next = Math.min(Math.max(Number(value) || 1, 1), this.totalPages);
      if (next === this.safePage) return;
      this.$emit('update:page', next);
      this.$emit('change', next);
    },
    // vue-awesome-paginate fires both `update:modelValue` and `click`; route them
    // through a single guarded emitter so the parent never gets a duplicate event.
    onPageModel(value) {
      this.emitPage(value);
    },
    onPageClick(value) {
      this.emitPage(value);
    },
    onPerPageChange(event) {
      const value = Number(event.target.value);
      if (!value || value === this.perPage) return;
      this.$emit('update:perPage', value);
      this.$emit('change-per-page', value);
    },
  },
};
</script>

<style scoped>
.skd-pagination {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
}

.skd-pagination__summary {
  text-align: right;
  color: var(--color-text-muted);
  font-size: 13px;
  font-weight: 500;
}

.skd-pagination__controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
}

/* page buttons and the per-page selector stay grouped together on the right */
.skd-pagination__perpage {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* ------------------------- paginate buttons ------------------------- */
.skd-pagination :deep(.pagination-container) {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.skd-pagination :deep(.paginate-buttons) {
  height: 34px;
  min-width: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.skd-pagination :deep(.number-buttons:hover) {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.skd-pagination :deep(.active-page) {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-text-on-primary);
}

.skd-pagination :deep(.active-page:hover) {
  background: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
  color: var(--color-text-on-primary);
}

/* ----------------------------- prev / next ----------------------------- */
.skd-pagination :deep(.back-button),
.skd-pagination :deep(.next-button) {
  padding: 0 12px;
  color: var(--color-text-secondary);
}

.skd-pagination :deep(.back-button:hover),
.skd-pagination :deep(.next-button:hover) {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.skd-pagination__nav {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
}

.skd-pagination__nav .pi {
  font-size: 10px;
}

/* disabled prev / next (auto-classed by the library at the range ends) */
.skd-pagination :deep(.disabled-back-button),
.skd-pagination :deep(.disabled-next-button),
.skd-pagination :deep(.disabled-paginate-buttons) {
  opacity: .55;
  cursor: not-allowed;
  background: var(--color-bg-muted);
  border-color: var(--color-border);
  color: var(--color-text-muted);
}

.skd-pagination :deep(.disabled-back-button:hover),
.skd-pagination :deep(.disabled-next-button:hover) {
  background: var(--color-bg-muted);
  border-color: var(--color-border);
  color: var(--color-text-muted);
}

/* ellipsis / jump breakpoints render as plain muted dots */
.skd-pagination :deep(.starting-breakpoint-button),
.skd-pagination :deep(.ending-breakpoint-button),
.skd-pagination :deep(.backward-jump-button),
.skd-pagination :deep(.forward-jump-button) {
  border-color: transparent;
  background: transparent;
  color: var(--color-text-muted);
  min-width: 22px;
}

/* ------------------------------ per page ------------------------------ */
.skd-pagination__select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.skd-pagination__select {
  height: 34px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 0 30px 0 12px;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.skd-pagination__select-icon {
  position: absolute;
  right: 10px;
  font-size: 11px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.skd-pagination__perpage-label {
  color: var(--color-text-muted);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

@media (max-width: 640px) {
  .skd-pagination__controls {
    justify-content: center;
  }
}
</style>
